import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useIntercom } from 'react-use-intercom';
import { useTheme } from 'styled-components';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';

import { GlobalStyles } from 'styled/GlobalStyles';

import { Header } from 'components/Header';
import { CampaignCookie, UTMCookie, ConsentCookie } from 'components/Cookie';
import { Datadog } from 'components/Datadog';
import { VWO } from 'components/VWO';
import { SharesightCountryCookie } from 'components/Cookie/SharesightCountryCookie';
import { CookiePreferencesBanner } from 'components/CookiePreferences';

import { Footer } from 'components/Footer';
import type { FC, ReactNode } from 'react';

export interface LayoutProps {
  children: ReactNode;
  /** Used in very rare scenarios, e.g. avoiding two consecutive cta's on mobile in the exec team page */
  hideFooterCta?: boolean;
}

export const Layout: FC<LayoutProps> = ({ children, hideFooterCta = false }) => {
  const { boot, shutdown } = useIntercom();
  const { breakpoints } = useTheme();
  const isMobile = !!useMediaQuery(breakpoints.down('sm'));

  useEffect(() => {
    // initialises intercom across all non-mobile pages
    if (!isMobile) boot();
    else shutdown();
  }, [boot, shutdown, isMobile]);

  return (
    <>
      <GlobalStyles />

      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      {/* Initialises VWO for a/b testing */}
      <VWO />
      {/* Initialises datadog rum */}
      <Datadog />
      {/* Sets a cookie for tracking affiliates and campaigns across sharesight.com sites. */}
      <CampaignCookie />
      {/* Sets a cookie for UTM parameters across sharesight.com sites. */}
      <UTMCookie />
      {/* Sets a cookie for cookie consent preferences across sharesight.com sites. */}
      <ConsentCookie />
      {/* Sets a cookie for sharesight_country across sharesight.com sites. */}
      <SharesightCountryCookie />

      <Header />
      <main>{children}</main>

      <Footer hideCta={hideFooterCta} />
      <CookiePreferencesBanner />
    </>
  );
};
